import moment from 'moment';

import type {
  QuestionnaireHeaderMetadata,
  QuestionnaireHeaderMetadataSource,
} from './types';
import type { TFunction } from 'i18next';

type HeaderTitleTranslationKey =
  | 'assessmentNotes'
  | 'billingClaim'
  | 'btSupervisionNotes'
  | 'clientDetailsPage'
  | 'clientSupervisionNotes'
  | 'createInvoice'
  | 'dataDashboard'
  | 'groupSupervisionNotes'
  | 'historicalDocuments'
  | 'historicalDocumentsCalendarView'
  | 'home'
  | 'intakeQuestionnaire'
  | 'invoiceDetail'
  | 'invoiceInfo'
  | 'invoices'
  | 'onboardingQuestionnaire'
  | 'parentSupervisionNotes'
  | 'previousNotes'
  | 'providerDetailsPage'
  | 'sessionNotes'
  | 'sessionNotesReview'
  | 'timesheet'
  | 'treatmentPlan';

export const getQuestionnaireHeaderMetadata = (
  details: QuestionnaireHeaderMetadataSource,
): QuestionnaireHeaderMetadata => ({
  author: details.general_info?.provider_full_name ?? '',
  created: details.date_row_created
    ? moment(details.date_row_created).format('MMMM Do, YYYY')
    : '',
  child: [details.general_info?.client_first_name, details.general_info?.client_last_name]
    .filter(Boolean)
    .join(' '),
});

export function getHeaderPageTitle(
  t: TFunction,
  title: HeaderTitleTranslationKey,
): string {
  return String(t(title));
}

export function getHeaderTitleForPath(
  t: TFunction,
  pathname: string,
): string {
  if (!pathname) return '';

  let routerName = pathname;
  routerName = pathname.includes('/sessions') ? '/sessions' : routerName;
  routerName = pathname.includes('/review') ? '/review' : routerName;
  routerName = pathname.includes('/client-supervisions') ? '/client-supervisions' : routerName;
  routerName = pathname.includes('/invoice') ? '/invoice' : routerName;
  routerName = pathname.includes('/invoice-list') ? '/invoice-list' : routerName;
  routerName = pathname.includes('/invoice-payment') ? '/invoice-payment' : routerName;
  routerName = pathname.includes('/invoice-builder') ? '/invoice-builder' : routerName;
  routerName = pathname.includes('/bt-supervisions') ? '/bt-supervisions' : routerName;
  routerName = pathname.includes('/parent-supervisions') ? '/parent-supervisions' : routerName;
  routerName = pathname.includes('/group-parent-training') ? '/group-parent-training' : routerName;
  routerName = pathname.includes('/assessment-note') ? '/assessment-note' : routerName;
  routerName = pathname.includes('/treatment-plan') ? '/treatment-plan' : routerName;
  routerName = pathname.includes('/billings/claims') ? '/billings/claims' : routerName;
  routerName = pathname.includes('/historical-documents') ? '/historical-documents' : routerName;
  routerName = pathname.includes('/historical-documents-calendar-view')
    ? '/historical-documents-calendar-view'
    : routerName;
  routerName = pathname.includes('client-details-page') ? '/client-details-page' : routerName;
  routerName = pathname.includes('treatment-plan-management')
    ? '/treatment-plan-management'
    : routerName;
  routerName = pathname.includes('admin/provider') ? '/provider-details-page' : routerName;
  routerName = pathname.includes('/questionnaires/onboarding')
    ? '/questionnaires/onboarding'
    : routerName;
  routerName = pathname.includes('/questionnaires/intake')
    ? '/questionnaires/intake'
    : routerName;

  switch (routerName) {
    case '/':
    case '/dashboard':
      return getHeaderPageTitle(t, 'home');
    case '/invoice':
      return getHeaderPageTitle(t, 'invoiceInfo');
    case '/invoice-builder':
      return getHeaderPageTitle(t, 'createInvoice');
    case '/invoice-list':
      return getHeaderPageTitle(t, 'invoices');
    case '/invoice-payment':
      return getHeaderPageTitle(t, 'invoiceDetail');
    case '/new-session':
    case '/sessions':
      return getHeaderPageTitle(t, 'sessionNotes');
    case '/review':
      return getHeaderPageTitle(t, 'sessionNotesReview');
    case '/historical-notes':
      return getHeaderPageTitle(t, 'previousNotes');
    case '/timesheet':
      return getHeaderPageTitle(t, 'timesheet');
    case '/assessment-note':
      return getHeaderPageTitle(t, 'assessmentNotes');
    case '/client-supervisions':
      return getHeaderPageTitle(t, 'clientSupervisionNotes');
    case '/bt-supervisions':
      return getHeaderPageTitle(t, 'btSupervisionNotes');
    case '/parent-supervisions':
      return getHeaderPageTitle(t, 'parentSupervisionNotes');
    case '/group-parent-training':
      return getHeaderPageTitle(t, 'groupSupervisionNotes');
    case '/treatment-plan':
      return getHeaderPageTitle(t, 'treatmentPlan');
    case '/questionnaires/onboarding':
      return getHeaderPageTitle(t, 'onboardingQuestionnaire');
    case '/questionnaires/intake':
      return getHeaderPageTitle(t, 'intakeQuestionnaire');
    case '/data-dashboard':
      return getHeaderPageTitle(t, 'dataDashboard');
    case '/billings/claims':
    case '/billings/claims/':
      return getHeaderPageTitle(t, 'billingClaim');
    case '/historical-documents':
      return getHeaderPageTitle(t, 'historicalDocuments');
    case '/historical-documents-calendar-view':
      return getHeaderPageTitle(t, 'historicalDocumentsCalendarView');
    case '/client-details-page':
      return getHeaderPageTitle(t, 'clientDetailsPage');
    case '/provider-details-page':
      return getHeaderPageTitle(t, 'providerDetailsPage');
    default:
      return '';
  }
}

export function getHeaderSessionStatusLabel(
  t: TFunction,
  status: string,
): string {
  switch (status.toLowerCase().split(' ').join('_')) {
    case 'started':
      return String(t('sessionStatus.started', { ns: 'common' }));
    case 'awaiting_review':
      return String(t('sessionStatus.awaiting_review', { ns: 'common' }));
    case 'edits_requested':
      return String(t('sessionStatus.edits_requested', { ns: 'common' }));
    case 'completed':
      return String(t('sessionStatus.completed', { ns: 'common' }));
    case 'awaiting_guardian_signature':
      return String(t('sessionStatus.awaiting_guardian_signature', { ns: 'common' }));
    case 'awaiting_finalization':
      return String(t('sessionStatus.awaiting_finalization', { ns: 'common' }));
    default:
      return status;
  }
}

export function getHeaderInvoiceStatusLabel(
  t: TFunction,
  status: string,
): string {
  switch (status.split(' ').join('_').toLowerCase()) {
    case 'paid':
      return String(t('invoiceStatus.paid', { ns: 'invoice' }));
    case 'not_paid':
      return String(t('invoiceStatus.not_paid', { ns: 'invoice' }));
    case 'partial_payment':
      return String(t('invoiceStatus.partial_payment', { ns: 'invoice' }));
    case 'in_progress':
      return String(t('invoiceStatus.in_progress', { ns: 'invoice' }));
    default:
      return status;
  }
}

export function getHeaderAuthorName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  return [firstName, lastName].filter(Boolean).join(' ');
}
